import { Button, DateInput, DateTimeUtils, generateResolver, Modal, NestedDropdown, Notification, STATUS_TYPES, TimeInput, VALIDATORS, yup } from "dyl-components";
import React, { useEffect, useState } from "react";
import { Checkbox, Form, Header, Icon, Segment } from "semantic-ui-react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { PhoneUtil } from "utils";
import pbxConfigActions from "actions/pbx_config";
import Utils from "shared/EventForm/Utils";
import companyActions from "actions/company";

const AddHolidayScheduleForm = ({ handleClose, holidaySchedule, actionType }) => {
    const addHolidayScheduleSchema = {
        name: yup.string().required("This is a required field").minlength(2).maxlength(64).simple_alphanumeric().no_excessive_whitespaces(),
        description: VALIDATORS.DESCRIPTION().noemoji(),
        schedule: yup.array().of(
            yup.object().shape({
                name: yup.string().required("This is a required field").minlength(2).maxlength(64).simple_alphanumeric().no_excessive_whitespaces(),

                startDate: yup
                    .string()
                    .required("Start Date is required")
                    .test(
                        "is-before-end",
                        "Start date must be before or equal to end date",
                        function (value) {
                            const { endDate } = this.parent;
                            if (!endDate || !value) return true;
                            return !DateTimeUtils.dateIsBeforeAnotherDate(endDate, value, 'day', DateTimeUtils.WORD_DATE_FORMAT);
                        }
                    ),

                startTime: yup.string().when(["allDay"], {
                    is: false,
                    then: (schema) => schema.required("Start Time is required")
                        .test(
                            "is-before-end-time",
                            "Start time must be before end time",
                            function (value) {
                                const { startDate, endDate, endTime } = this.parent;
                                if (!startDate || !endDate || !value || !endTime) return true;
                                if (startDate !== endDate) return true;
                                const current_start = `${startDate} ${value}`;
                                const current_end = `${endDate} ${endTime}`;
                                return !DateTimeUtils.dateIsBeforeAnotherDate(current_end, current_start, 'minutes', DateTimeUtils.WORD_DATETIME_FORMAT, true);
                            }
                        )
                }),

                endDate: yup
                    .string()
                    .required("End Date is required")
                    .test(
                        "is-after-start",
                        "End date must be after or equal to start date",
                        function (value) {
                            const { startDate } = this.parent;
                            if (!startDate || !value) return true;
                            return !DateTimeUtils.dateIsBeforeAnotherDate(value, startDate, 'day', DateTimeUtils.WORD_DATE_FORMAT);
                        }
                    ),

                endTime: yup.string().when(["allDay"], {
                    is: false,
                    then: (schema) => schema.required("End Time is required")
                        .test(
                            "is-after-start-time",
                            "End time must be after start time",
                            function (value) {
                                const { startDate, endDate, startTime } = this.parent;
                                if (!startDate || !endDate || !value || !startTime) return true;
                                if (startDate !== endDate) return true;
                                const current_start = `${startDate} ${startTime}`;
                                const current_end = `${endDate} ${value}`;
                                return !DateTimeUtils.dateIsBeforeAnotherDate(current_end, current_start, 'minutes', DateTimeUtils.WORD_DATETIME_FORMAT, true);
                            }
                        )
                }),

                destination: yup.object().shape({
                    parent_value: yup.string()
                        .required('Destination is required'),

                    child_value: yup.number()
                        .typeError('child_value must be a number')
                        .required('Destination is required'),
                }).required('Destination is required')
            })
        ).min(1, "At least one holiday is required")
            .test(
                "no-overlapping-dates",
                "Date conflict detected",
                function (schedule) {
                    if (!schedule || schedule.length < 2) return true;

                    const errors = schedule.flatMap((firstHoliday, firstIndex) =>
                        schedule.slice(firstIndex + 1).reduce((errors, secondHoliday, offset) => {
                            const secondIndex = firstIndex + 1 + offset;

                            if (
                                !firstHoliday.startDate || !firstHoliday.endDate ||
                                !secondHoliday.startDate || !secondHoliday.endDate
                            ) return errors;

                            const firstStart = DateTimeUtils.getUnixTime(firstHoliday.startDate, DateTimeUtils.WORD_DATE_FORMAT);
                            const firstEnd = DateTimeUtils.getUnixTime(firstHoliday.endDate, DateTimeUtils.WORD_DATE_FORMAT);
                            const secondStart = DateTimeUtils.getUnixTime(secondHoliday.startDate, DateTimeUtils.WORD_DATE_FORMAT);
                            const secondEnd = DateTimeUtils.getUnixTime(secondHoliday.endDate, DateTimeUtils.WORD_DATE_FORMAT);

                            const hasOverlap = firstStart <= secondEnd && secondStart <= firstEnd;

                            if (hasOverlap) {
                                const secondStartInFirst = secondStart >= firstStart && secondStart <= firstEnd;
                                const secondEndInFirst = secondEnd >= firstStart && secondEnd <= firstEnd;
                                const secondContainsFirst = !secondStartInFirst && !secondEndInFirst;

                                if (secondStartInFirst || secondContainsFirst) {
                                    errors.push(this.createError({
                                        path: `schedule[${secondIndex}].startDate`,
                                        message: "Date range overlaps with another holiday. Schedules cannot overlap."
                                    }));
                                }
                                if (secondEndInFirst || secondContainsFirst) {
                                    errors.push(this.createError({
                                        path: `schedule[${secondIndex}].endDate`,
                                        message: "Date range overlaps with another holiday. Schedules cannot overlap."
                                    }));
                                }
                            }

                            return errors;
                        }, [])
                    );

                    if (errors.length) {
                        throw new yup.ValidationError(errors);
                    }

                    return true;
                }
            )
    }
    const { control, handleSubmit, formState: { isValid, isDirty }, watch, setValue, trigger, reset, setError, clearErrors } = useForm({
        mode: "onChange",
        defaultValues: {
            name: '',
            description: '',
            schedule: [{
                id: 1,
                name: "",
                startDate: "",
                startTime: "",
                endDate: "",
                endTime: "",
                allDay: false,
                destination: { parent_value: "", child_value: null }
            }]
        },
        resolver: generateResolver(addHolidayScheduleSchema)
    });
    const { fields, append, remove } = useFieldArray({
        name: "schedule",
        control,
        keyName: "id"
    })
    const isCheckingForDuplicates = useSelector(state => state.pbx_config.isReadingUniqueName);
    const [typingTimeout, setTypingtimeout] = useState(null);

    const company_timezone = useSelector(state => state?.company?.company?.timezone);
    
    const {
        destinations: destinationTypes,
        isReadingDestinations,
        isReadingHolidaySchedule,
        isCreatingHolidaySchedule,
        isUpdatingHolidaySchedule
    } = useSelector((state) => state.pbx_config);
    const dispatch = useDispatch()
    const [destinationOptions, setDestinationOptions] = useState([]);
    const timeDropdownDefaults = DateTimeUtils.getTimeDropdownDefaults();
    const onSubmit = async (data) => {
        try {
            const payload = {
                label: data.name,
                description: data.description,
                dates: data.schedule.map((item) => ({
                    start_time: item.allDay ? DateTimeUtils.getUnixTime(`${item.startDate} 12:00 am`, DateTimeUtils.WORD_DATETIME_FORMAT) : Utils.formatDate({ all_day: item.allDay, date: item.startDate, date_time: item.startTime, tz: company_timezone })?.date_time,
                    end_time: item.allDay ? DateTimeUtils.getUnixTime(`${item.endDate} 11:59 pm`, DateTimeUtils.WORD_DATETIME_FORMAT) : Utils.formatDate({ all_day: item.allDay, date: item.endDate, date_time: item.endTime, tz: company_timezone })?.date_time,
                    name: item.name,
                    destination_id: item.destination.child_value,
                    all_day: item.allDay
                }))
            }
            if (actionType === "edit") {
                await dispatch(pbxConfigActions.updateHolidaySchedule(holidaySchedule?.id, payload))
            } else {
                await dispatch(pbxConfigActions.createHolidaySchedule(payload))
            }
            Notification.alert(`Holiday Schedule ${actionType === "edit" ? "Updated" : "Created"} Successfully!`, STATUS_TYPES.SUCCESS);
            handleClose();
            dispatch(pbxConfigActions.getHolidaySchedules());
        } catch (error) {
            Notification.alert(`Failed to ${actionType === "edit" ? "update" : "create"} Holiday Schedule`, STATUS_TYPES.ERROR, true);
        }
    }


    useEffect(() => {
        if (destinationTypes) {
            const destinationTypesAux = [...PhoneUtil.DESTINATION_TYPES_OPTIONS];
            const destinationOptionsAux = destinationTypesAux.map((destinationType) => {
                const { key } = destinationType;
                const options = destinationTypes[key]?.map(({ destination_id, label }) => (
                    { key: destination_id, value: destination_id, text: label }
                )) || []
                return { ...destinationType, options };
            })
            setDestinationOptions(destinationOptionsAux);
        }
    }, [destinationTypes])

    useEffect(() => {
        dispatch(pbxConfigActions.getDestinations());
        dispatch(companyActions.readCompany());
    }, [dispatch])

    useEffect(() => {
        if (actionType === "edit" || actionType === "duplicate") {
            reset({
                name: actionType === "duplicate" ? `Duplicate of ${holidaySchedule?.label}` : holidaySchedule?.label,
                description: holidaySchedule?.description || "",
                schedule: holidaySchedule?.dates?.data?.map((date) => {
                    const start_epoch = date.all_day
                        ? DateTimeUtils.getUnixTime(
                            DateTimeUtils.formatEpoch(date.start_time, DateTimeUtils.WORD_DATE_FORMAT),
                            DateTimeUtils.WORD_DATE_FORMAT
                        )
                        : date.start_time;

                    const end_epoch = date.all_day
                        ? DateTimeUtils.getUnixTime(
                            DateTimeUtils.formatEpoch(date.end_time, DateTimeUtils.WORD_DATE_FORMAT),
                            DateTimeUtils.WORD_DATE_FORMAT
                        )
                        : date.end_time;

                    return {
                        name: date.name,
                        startDate: DateTimeUtils.formatEpoch(start_epoch, DateTimeUtils.WORD_DATE_FORMAT),
                        startTime: DateTimeUtils.formatEpoch(start_epoch),
                        endDate: DateTimeUtils.formatEpoch(end_epoch, DateTimeUtils.WORD_DATE_FORMAT),
                        endTime: DateTimeUtils.formatEpoch(end_epoch),
                        allDay: date.all_day || false,
                        destination: {
                            parent_value: date.destination?.type,
                            child_value: date.destination?.destination_id
                        }
                    };
                })
            })
        }
    }, [holidaySchedule, actionType, reset])

    const triggerDateValidations = (excludeIndex) => {
        trigger(fields.map((field, index) => {
            if (index === excludeIndex) return [];
            return [
                field.startDate && `schedule[${index}].startDate`,
                field.endDate && `schedule[${index}].endDate`
            ].filter(Boolean);
        }).flat())
    }

    return (
        <React.Fragment>
            <Modal.Header>
                {actionType === "edit" ? "Edit Holiday Schedule" : "Add Holiday Schedule"}
            </Modal.Header>
            {isReadingHolidaySchedule ? (
                <Segment loading basic></Segment>
            ) : (
                [<Modal.Content>
                    <Form loading={isReadingHolidaySchedule}>
                        <Form.Field width={5}>
                            <Controller
                                control={control}
                                name="name"
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Input
                                        name={name}
                                        value={value}
                                        onChange={async (_, { name, value }) => {
                                            if (typingTimeout) {
                                                clearTimeout(typingTimeout)
                                            }
                                            onChange(value);
                                            const isValid = await addHolidayScheduleSchema.name.isValid(value);
                                            if (isValid && value !== '' && value !== holidaySchedule?.label) {
                                                setTypingtimeout(setTimeout(async () => {
                                                    await clearErrors(name);
                                                    const isDuplicated = await dispatch(pbxConfigActions.isDuplicatedName({ label: value, type: "holiday_schedule" }))
                                                    if (isDuplicated) {
                                                        await setError(name, {
                                                            type: 'unique_product',
                                                            message: 'Holiday Schedule name already exists!'
                                                        })
                                                    }
                                                }));
                                            }
                                        }}
                                        label="Holiday Schedule Name"
                                        placeholder='Type Holiday Schedule Name'
                                        required
                                        error={error?.message}
                                        loading={isCheckingForDuplicates}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Form.Field width={8}>
                            <Controller
                                control={control}
                                name="description"
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Input
                                        name={name}
                                        value={value}
                                        label="Holiday Schedule Description"
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                        placeholder='Type Description'
                                        error={error?.message}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Header as="h3" content="Holidays" color="primary" />
                        {
                            fields.map((field, index) => (
                                <Form.Group key={field.id}>
                                    <Form.Field width={5}>
                                        <Controller
                                            control={control}
                                            name={`schedule[${index}].name`}
                                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                                <Form.Input
                                                    name={name}
                                                    value={value}
                                                    label="Holiday Name"
                                                    required
                                                    onChange={(_, { value }) => { onChange({ target: { name, value } }); } }
                                                    placeholder='Type Holiday Name'
                                                    error={error?.message} />
                                            )} />
                                    </Form.Field>
                                    <Form.Field width={5}>
                                        <Controller
                                            name={`schedule[${index}].startDate`}
                                            control={control}
                                            render={({
                                                field: {
                                                    value, onChange, name
                                                }, fieldState: { error }
                                            }) => (
                                                <Form.Field
                                                    control={DateInput}
                                                    value={value}
                                                    onBlur={() => {
                                                        if (!DateTimeUtils.isValid(value, DateTimeUtils.WORD_DATE_FORMAT)) {
                                                            onChange({ target: { name, value: timeDropdownDefaults.startDate } });
                                                            trigger([`schedule[${index}].endDate`, `schedule[${index}].startTime`, `schedule[${index}].endTime`]);
                                                            triggerDateValidations(index);
                                                        }
                                                    } }
                                                    onChange={(_, { value: new_value }) => {
                                                        onChange({ target: { name, value: new_value } });
                                                        trigger([`schedule[${index}].endDate`, `schedule[${index}].startTime`, `schedule[${index}].endTime`]);
                                                        triggerDateValidations(index);
                                                    } }
                                                    placeholder="Select Start Date"
                                                    label="Start Date/Time"
                                                    required
                                                    dateFormat={DateTimeUtils.WORD_DATE_FORMAT}
                                                    error={error?.message} />
                                            )} />
                                        <div className="AddHolidayScheduleForm__AllDayCheckbox">
                                            <Checkbox
                                                label="All Day"
                                                checked={watch(`schedule[${index}].allDay`)}
                                                onChange={(_, { checked }) => {
                                                    if (checked) {
                                                        setValue(`schedule[${index}].startTime`, timeDropdownDefaults.startTime, { shouldDirty: true });
                                                        setValue(`schedule[${index}].endTime`, timeDropdownDefaults.endTime, { shouldDirty: true });
                                                    }

                                                    setValue(`schedule[${index}].allDay`, checked, { shouldDirty: true });

                                                    trigger([
                                                        `schedule[${index}].startTime`,
                                                        `schedule[${index}].endTime`
                                                    ]);
                                                } } />
                                        </div>
                                    </Form.Field>
                                    <Form.Field width={5}>
                                        <Controller
                                            name={`schedule[${index}].startTime`}
                                            control={control}
                                            render={({
                                                field: {
                                                    value, onChange
                                                }, fieldState: { error }
                                            }) => (
                                                <Form.Field
                                                    control={TimeInput}
                                                    value={value}
                                                    disabled={watch(`schedule[${index}].allDay`)}
                                                    onChange={(_, { value }) => {
                                                        onChange(value);
                                                        trigger(`schedule[${index}].endTime`);
                                                    } }
                                                    label={() => {
                                                        return <div className="AddHolidayScheduleForm__Time">time</div>;
                                                    } }
                                                    timeFormat={"AMPM"}
                                                    placeholder="Select Start Time"
                                                    error={error?.message} />
                                            )} />
                                    </Form.Field>
                                    <Form.Field width={5}>
                                        <Controller
                                            control={control}
                                            name={`schedule[${index}].endDate`}
                                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                                <Form.Field
                                                    control={DateInput}
                                                    value={value}
                                                    onBlur={() => {
                                                        if (!DateTimeUtils.isValid(value, DateTimeUtils.WORD_DATE_FORMAT)) {
                                                            onChange({ target: { name, value: timeDropdownDefaults.endDate } });
                                                            trigger([`schedule[${index}].endTime`]);
                                                            triggerDateValidations(index);
                                                        }
                                                    } }
                                                    onChange={(_, { value: new_value }) => {
                                                        onChange({ target: { name, value: new_value } });
                                                        trigger([`schedule[${index}].endTime`, `schedule[${index}].startDate`]);
                                                        triggerDateValidations(index);
                                                    } }
                                                    placeholder="Select End Date"
                                                    label="End Date/Time"
                                                    required
                                                    dateFormat={DateTimeUtils.WORD_DATE_FORMAT}
                                                    error={error?.message} />
                                            )} />
                                    </Form.Field>
                                    <Form.Field width={5}>
                                        <Controller
                                            name={`schedule[${index}].endTime`}
                                            control={control}
                                            render={({
                                                field: {
                                                    value, onChange
                                                }, fieldState: { error }
                                            }) => (
                                                <Form.Field
                                                    control={TimeInput}
                                                    value={value}
                                                    onChange={(_, { value }) => {
                                                        onChange(value);
                                                        trigger(`schedule[${index}].startTime`);
                                                    } }
                                                    disabled={watch(`schedule[${index}].allDay`)}
                                                    label={() => {
                                                        return <div className="AddHolidayScheduleForm__Time">time</div>;
                                                    } }
                                                    placeholder="Select End Time"
                                                    timeFormat={"AMPM"}
                                                    error={error?.message} />
                                            )} />
                                    </Form.Field>
                                    <Form.Field width={5}>
                                        <div className="AddHolidayScheduleForm__Destination">
                                            <Controller
                                                control={control}
                                                name={`schedule[${index}].destination`}
                                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                                    <Form.Field
                                                        control={NestedDropdown}
                                                        loading={isReadingDestinations}
                                                        nested_options={destinationOptions}
                                                        child_value={value?.child_value}
                                                        parent_value={value?.parent_value}
                                                        onChange={(_, { parent_value, child_value }) => {
                                                            onChange({ target: { name, value: { parent_value, child_value } } });
                                                        } }
                                                        placeholder="Select destination"
                                                        display_parent
                                                        selection
                                                        label="Destination"
                                                        pointing='top'
                                                        required
                                                        error={error?.message} />
                                                )} />
                                        </div>
                                    </Form.Field>
                                    <Form.Field width={1} className="AddHolidayScheduleForm__DeleteButton">
                                        <Icon className="fas fa-trash" color="red" size="large" link onClick={() => {
                                            trigger("schedule");
                                            remove(index);
                                        } } disabled={fields.length === 1} />
                                    </Form.Field>
                                </Form.Group>
                            ))
                        }
                        <Header content="+ Add New Holiday" as="h5" color="primary" className="AddHolidayScheduleForm__AddNew" onClick={() => {
                            append({
                                name: "",
                                startDate: "",
                                startTime: "",
                                endDate: "",
                                endTime: "",
                                allDay: false,
                                destination: null
                            })
                        }} />
                    </Form>
                </Modal.Content>,
                <Modal.Actions >
                    <Button
                        disabled={!isValid || isCreatingHolidaySchedule || isUpdatingHolidaySchedule || (actionType === "edit" && !isDirty)}
                        primary
                        onClick={handleSubmit(onSubmit)}
                        loading={isCreatingHolidaySchedule || isUpdatingHolidaySchedule}
                    >
                        Save
                    </Button>
                </Modal.Actions>]
            )}
        </React.Fragment>
    )
}

export default AddHolidayScheduleForm;
